<script lang="ts">
  /**
   * Сучасний relation-picker (cmd+k-стиль combobox): realtime fuzzy-пошук по
   * сервер-endpoint'у /lookup/:slug/search, клавіатурна навігація, multi-select
   * з chips, inline-create нового запису (custom-таблиці). Замінює старий
   * <select>/checkbox у LinkedRecordInput.
   */
  import Icon from '../../components/Icon.svelte';
  import { showAlert } from '../ui-alert';
  import {
    searchRelation,
    createDataRecord,
    fetchPublicDataTable,
    type RelationCard,
  } from '../data-tables-api';
  import { createPerson } from '../persons-api';
  import { resolveCard, primeCard, invalidateLookupCache } from './linked-cache';
  import RelationBubble from './RelationBubble.svelte';

  let {
    slug,
    cardinality = 'one',
    value,
    onChange,
    disabled = false,
  }: {
    slug: string;
    cardinality?: 'one' | 'many';
    value: unknown;
    onChange: (v: unknown) => void;
    disabled?: boolean;
  } = $props();

  const isMany = $derived(cardinality === 'many');
  const isCustom = $derived(slug !== 'users' && slug !== 'payments');

  /* ── selected ids + their cards ─────────────────────────────────────────── */
  const selectedIds = $derived.by((): string[] => {
    if (isMany) {
      if (Array.isArray(value)) return value.filter((v) => v != null && v !== '').map(String);
      if (value != null && value !== '') return [String(value)];
      return [];
    }
    const s = value != null && value !== '' ? String(value) : '';
    return s ? [s] : [];
  });
  const selectedSet = $derived(new Set(selectedIds));

  let chipCards = $state<Map<string, RelationCard>>(new Map());
  $effect(() => {
    const ids = selectedIds;
    let cancelled = false;
    Promise.all(ids.map(async (id) => [id, await resolveCard(slug, id)] as const)).then((pairs) => {
      if (!cancelled) chipCards = new Map(pairs);
    });
    return () => { cancelled = true; };
  });

  /* ── search state ───────────────────────────────────────────────────────── */
  let open = $state(false);
  let query = $state('');
  let results = $state<RelationCard[]>([]);
  let nextCursor = $state<string | null>(null);
  let loading = $state(false);
  let activeIndex = $state(0);
  let searchSeq = 0;
  let inputEl = $state<HTMLInputElement | null>(null);
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let creating = $state(false);

  /** Primary fieldKey цільової таблиці — для inline-create (custom). */
  let primaryKey = $state<string | null>(null);
  $effect(() => {
    if (!isCustom) { primaryKey = null; return; }
    let cancelled = false;
    fetchPublicDataTable(slug)
      .then((t) => { if (!cancelled) primaryKey = t.fields.find((f) => f.primary)?.fieldKey ?? null; })
      .catch(() => { if (!cancelled) primaryKey = null; });
    return () => { cancelled = true; };
  });

  // Inline-create: users (createPerson) або custom (createDataRecord з primary).
  // payments — не підтримується.
  const canCreate = $derived(
    (slug === 'users' || (isCustom && !!primaryKey)) && query.trim().length > 0 && !creating,
  );
  /** Кількість опцій у дропдауні (results + рядок create). */
  const optionCount = $derived(results.length + (canCreate ? 1 : 0));
  const createRowIndex = $derived(results.length); // індекс create-рядка

  function runSearch(reset = true) {
    const seq = ++searchSeq;
    loading = true;
    const cursor = reset ? undefined : (nextCursor ?? undefined);
    searchRelation(slug, { q: query.trim(), limit: 25, cursor })
      .then((r) => {
        if (seq !== searchSeq) return; // stale
        results = reset ? r.items : [...results, ...r.items];
        nextCursor = r.nextCursor;
        if (reset) activeIndex = 0;
        loading = false;
      })
      .catch(() => {
        if (seq !== searchSeq) return;
        if (reset) results = [];
        loading = false;
      });
  }

  function openDropdown() {
    if (disabled) return;
    open = true;
    runSearch(true);
  }
  function onInput() {
    open = true;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => runSearch(true), 200);
  }
  function close() {
    open = false;
    query = '';
    results = [];
    nextCursor = null;
  }

  function commit(next: string[]) {
    onChange(next.length > 0 ? (isMany ? next : next[0]) : null);
  }
  function pick(id: string) {
    if (isMany) {
      const next = selectedSet.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
      commit(next);
      query = '';
      runSearch(true);
      inputEl?.focus();
    } else {
      commit([id]);
      close();
    }
  }
  function removeId(id: string) {
    commit(selectedIds.filter((x) => x !== id));
  }

  async function doCreate() {
    const title = query.trim();
    if (!title) return;
    if (slug !== 'users' && (!isCustom || !primaryKey)) return;
    creating = true;
    try {
      const { id } =
        slug === 'users'
          ? await createPerson(title)
          : await createDataRecord(slug, { [primaryKey!]: title });
      invalidateLookupCache(slug);
      primeCard(slug, {
        id, title, subtitle: null, email: null, phone: null, telegram: null,
        type: slug, initials: title.slice(0, 2).toUpperCase(), label: title,
      });
      pick(id);
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e), 'Не вдалося створити запис');
    } finally {
      creating = false;
    }
  }

  function onKeydown(e: KeyboardEvent) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { openDropdown(); e.preventDefault(); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { activeIndex = Math.min(activeIndex + 1, optionCount - 1); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { activeIndex = Math.max(activeIndex - 1, 0); e.preventDefault(); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if ((e.metaKey || e.ctrlKey) && canCreate) void doCreate();
      else if (canCreate && activeIndex === createRowIndex) void doCreate();
      else if (results[activeIndex]) pick(results[activeIndex]!.id);
    } else if (e.key === 'Escape') { e.preventDefault(); close(); inputEl?.blur(); }
    else if (e.key === 'Backspace' && query === '' && selectedIds.length > 0) {
      removeId(selectedIds[selectedIds.length - 1]!);
    }
  }

  function onTriggerClick() {
    if (disabled) return;
    if (!open) openDropdown();
    inputEl?.focus();
  }

  // Закриття при кліку поза компонентом.
  let rootEl = $state<HTMLElement | null>(null);
  function onWindowPointer(e: MouseEvent) {
    if (open && rootEl && !rootEl.contains(e.target as Node)) close();
  }
</script>

<svelte:window onmousedown={onWindowPointer} />

<div class="relative" bind:this={rootEl}>
  <!-- Trigger: chips + search input -->
  <div
    class="flex flex-wrap items-center gap-1 rounded-md border bg-white px-1.5 py-1 text-sm transition-colors
      {disabled ? 'opacity-60' : 'border-[var(--color-border-3)] focus-within:ring-1 focus-within:ring-[var(--color-pu)]'}"
    onclick={onTriggerClick}
    role="presentation"
  >
    {#each selectedIds as id (id)}
      <RelationBubble card={chipCards.get(id) ?? null} {slug} {id} dense removable {disabled} onRemove={() => removeId(id)} />
    {/each}
    {#if !disabled}
      <input
        bind:this={inputEl}
        bind:value={query}
        oninput={onInput}
        onfocus={openDropdown}
        onkeydown={onKeydown}
        placeholder={selectedIds.length === 0 ? 'Пошук або створення…' : ''}
        class="min-w-[80px] flex-1 bg-transparent px-1 py-0.5 text-sm focus:outline-none"
        aria-expanded={open}
        role="combobox"
        aria-controls="relation-listbox"
        aria-autocomplete="list"
      />
    {/if}
    {#if !disabled}
      <span class="ml-auto inline-flex flex-none text-[var(--color-text-muted-2)]"><Icon name="chevronDown" size={14} /></span>
    {/if}
  </div>

  <!-- Dropdown -->
  {#if open && !disabled}
    <div
      id="relation-listbox"
      class="absolute z-[60] mt-1 w-full overflow-hidden rounded-lg border border-[var(--color-border-3)] bg-white shadow-xl"
      role="listbox"
    >
      <div class="flex items-center gap-1.5 border-b border-[var(--color-border-3)] px-2.5 py-1.5 text-[var(--color-text-muted-2)]">
        <Icon name="search" size={13} />
        <span class="text-[11px]">{loading ? 'Пошук…' : query ? `Результати для «${query}»` : 'Останні записи'}</span>
      </div>

      <div class="max-h-56 overflow-y-auto py-1">
        {#each results as r, i (r.id)}
          <button
            type="button"
            role="option"
            aria-selected={selectedSet.has(r.id)}
            onmouseenter={() => (activeIndex = i)}
            onclick={() => pick(r.id)}
            class="flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left {i === activeIndex ? 'bg-[var(--color-bg-3)]' : ''}"
          >
            {#if isMany}
              <span class="grid h-4 w-4 flex-none place-items-center rounded border
                {selectedSet.has(r.id) ? 'bg-[var(--color-pu)] border-[var(--color-pu)] text-white' : 'border-[var(--color-border-3)]'}">
                {#if selectedSet.has(r.id)}<Icon name="check" size={11} />{/if}
              </span>
            {/if}
            <span class="grid h-6 w-6 flex-none place-items-center rounded-full text-[10px] font-bold text-white"
              style="background: {avatarColor(r)};">{r.initials}</span>
            <span class="flex min-w-0 flex-col leading-tight">
              <span class="truncate text-[12.5px] font-medium">{r.title}</span>
              {#if r.subtitle}<span class="truncate text-[10.5px] text-[var(--color-text-muted)]">{r.subtitle}</span>{/if}
            </span>
          </button>
        {/each}

        {#if results.length === 0 && !loading}
          <div class="px-3 py-3 text-center text-[11px] italic text-[var(--color-text-muted-2)]">
            {query ? 'Нічого не знайдено' : 'Записів ще немає'}
          </div>
        {/if}

        {#if nextCursor}
          <button
            type="button"
            onclick={() => runSearch(false)}
            class="w-full px-3 py-1.5 text-center text-[11px] font-semibold text-[var(--color-pu-3)] hover:bg-[var(--color-bg-3)]"
          >Завантажити ще</button>
        {/if}
      </div>

      {#if canCreate}
        <button
          type="button"
          role="option"
          aria-selected={activeIndex === createRowIndex}
          onmouseenter={() => (activeIndex = createRowIndex)}
          onclick={() => void doCreate()}
          class="flex w-full items-center gap-2 border-t border-[var(--color-border-3)] px-2.5 py-2 text-left text-[12px]
            {activeIndex === createRowIndex ? 'bg-purple-50' : ''}"
        >
          <span class="grid h-6 w-6 flex-none place-items-center rounded-full bg-[var(--color-pu)] text-white"><Icon name="add" size={13} /></span>
          <span class="truncate">Створити «<span class="font-semibold">{query.trim()}</span>»</span>
          {#if creating}<span class="ml-auto text-[10px] text-[var(--color-text-muted)]">…</span>{/if}
        </button>
      {/if}
    </div>
  {/if}
</div>

<script lang="ts" module>
  import type { RelationCard as RC } from '../data-tables-api';
  const COLORS = ['#7c4ddc', '#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626', '#db2777', '#4f46e5'];
  export function avatarColor(card: RC): string {
    let h = 0;
    for (const ch of card.id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return COLORS[h % COLORS.length]!;
  }
</script>
